import React, { useState, useEffect, useMemo } from "react";
import { Form, Popconfirm, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import CustomModal from "@/components/CustomModal";
import { StyledFlex, StyledSubTableContainer } from "@/common/styles";
import RulesCriteriaLocksPermitsForm from "@/pages/Security/Sections/StaffGroupForms/RulesCriteriaLocksPermitsForm";
import {
  fetchSecurityRulesCriteria,
  fetchSecurityRuleCriteriaById,
  createSecurityRuleCriteria,
  updateSecurityRuleCriteria,
  deleteSecurityRuleCriteria,
} from "@/apis/security/securityRulesCriteriaApi";
import { clearCurrentCriteria } from "@/slices/securityRulesCriteriaSlice";
import { fetchGlobalCriteria } from "@/apis/security/globalCriteriaApi";
import { fetchAllSecurityListOfStaff } from "@/apis/staff/staffApi";
import { setBookings } from "@/slices/bookingsSlice";
import {
  isLoggedInStaff,
  isNonEmptyVariable,
  refreshLoggedInUserSecurity,
} from "@/util/common_function";
import { v4 as uuidV4 } from "uuid";
import _ from "lodash";
import useDialog from "@/hooks/useDialog";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const SECURITY_RULES_CRITERIA_HEADERS = [
  { key: "criteriaName", text: "Criteria Name" },
  { key: "startingPoint", text: "Starting Point" },
  { key: "comment", text: "Comment" },
  { key: "allowSetting", text: "Allow" },
  { key: "denySetting", text: "Deny" },
];

// Stable reference: a fresh `{}` literal on every render re-fires the form's
// seed effect (deps: [initialValues]) and wipes in-progress Add input whenever
// this component re-renders (e.g. any redux dispatch re-renders the parent).
const EMPTY_INITIAL_VALUES = {};

const UserInfoCriteriaRulesControlling = ({ staffGroupsId, bookingsFieldsEnabled = true, startingPoint = "" }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { showDialog } = useDialog();

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). Reachable from both
  // /staffing (startingPoint="Staff") and /users (startingPoint="Users") —
  // per explicit product decision, each entry point persists its own layout.
  const {
    headers,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef: tableRef,
  } = useGridColumnConfig(
    `${startingPoint || "Staff"}:SecurityRulesCriteria`,
    SECURITY_RULES_CRITERIA_HEADERS
  );

  // const list = useSelector((s) => s.securityRulesCriteria.list) || [];
  // const list = useSelector((s) => s.staff.securityRulesCriteria) || [];
  const [list, setList] = useState([])
  const { bookings } = useSelector((s) => s?.bookings);

  const loadingList = useSelector((s) => s.securityRulesCriteria.listLoading);
  const current = useSelector((s) => s.securityRulesCriteria.current);
  const currentLoading = useSelector((s) => s.securityRulesCriteria.currentLoading);
  const saving = useSelector((s) => s.securityRulesCriteria.saving);
  const deleting = useSelector((s) => s.securityRulesCriteria.deleting);
  const globalCriteria = useSelector((s) => s.globalCriteria.list) || [];

  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");

  const criteriaMap = useMemo(
    () => Object.fromEntries(globalCriteria.map(c => [c.globalCriteriaID, c.criteriaName])),
    [globalCriteria]
  );
  const data = useMemo(
    () => list.map(row => ({
      ...row,
      criteriaName: criteriaMap[row.globalCriteriaID] || row.globalCriteriaID,
    })),
    [list, criteriaMap]
  );

  useEffect(() => {

    // dispatch(fetchSecurityRulesCriteria());
    // dispatch(clearCurrentCriteria());
    setSelectedRow(null);

    // dispatch(fetchGlobalCriteria());
  }, [dispatch]);
  useEffect(() => {
    if (isNonEmptyVariable(bookings?.securityRulesCriteria)) {
      let allRules = bookings?.securityRulesCriteria?.map((e, i) => {
        return {
          ...e,
          uuid: uuidV4(),
        }
      })
      console.log(allRules, "allRules")
      if (allRules?.length) {
        setList([...allRules])
      }
      else{
        setList([])
      }
    }
  }, [bookings]);
  const onRowClick = (row, index) => {
    setSelectedRow(row);
    if(index !== null && index !== undefined) {
      openEdit();
    }
  };

  const openAdd = () => {
    form.resetFields();
    dispatch(clearCurrentCriteria());
    setMode("add");
    setSelectedRow(null);
    setModalOpen(true);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    form.resetFields();
    dispatch(clearCurrentCriteria());
    dispatch(fetchSecurityRuleCriteriaById(selectedRow.securityRulesCriteriaID));
    setMode("edit");
    setModalOpen(true);
  };

   const openDelete = () => {
    if (!selectedRow) return;
    // setDeleteOpen(true);
    showDialog("confirm", {
      title: "Are you sure you want to delete this record?",
      onConfirm: () => confirmDelete(),
      // onCancel: () => { setIsLoading(false) }
    });
  };
  const confirmDelete = () => {
    if (!selectedRow) return;
    dispatch(deleteSecurityRuleCriteria(selectedRow.securityRulesCriteriaID)).then(() => {
      if (isLoggedInStaff(bookings?.staffId)) {
        refreshLoggedInUserSecurity(dispatch);
      }
    });
    let newList = _.cloneDeep(list)
    if (newList?.length) {
      newList = newList.filter((e, i) => {
        if (e.uuid != selectedRow?.uuid) {
          return e
        }
      })
      console.log({ newList })
      dispatch(
        setBookings({
          securityRulesCriteria: [...newList],
        })
      );
    }
    setSelectedRow(null);
  };

  const closeModal = () => {
    dispatch(clearCurrentCriteria());
    setModalOpen(false);
  };

  const handleSave = () => form.submit();

  const onFinish = values => {
    const payload = {
      ...values,
      dateAdded: values.dateAdded?.format("MM/DD/YYYY"),
      timeAdded: values.timeAdded?.format("HH:mm"),
      staffId: bookings?.staffId,
      ruleActive: ruleActive ? "true" : "false"
    };
    let newList = _.cloneDeep(list)
        // console.log({ payload })
        if (mode == "edit") {
          console.log({ selectedRow })
          if (newList?.length) {
            newList = newList.map((e, i) => {
              if (e.uuid == selectedRow?.uuid) {
                console.log({ ...e, ...payload }, "newList")
                return { ...e, ...payload }
              }
              return e
            })
            console.log({ newList })
          } 
        }
  if (mode == "add") {
      if (newList?.length == 0) {
        newList.push(payload)
        // setList([payload])
      } else {
        console.log({payload})
        newList=[...newList,payload]
        // setList([...list, payload])
      }
    }
    console.log({list})
        dispatch(
          setBookings({
            securityRulesCriteria: [...newList],
          })
        );

          closeModal();
  };

  return (
    <>
      <StyledSubTableContainer marginTop="25px">
        <StyledFlex justify="space-between" align="center">
          <h2>Criteria rules controlling access to data</h2>
          <StyledFlex gap="10px" marginBottom="5px" justify="end">
            <Button onClick={openAdd} loading={saving} disabled={!bookingsFieldsEnabled || isConfiguring}>Add</Button>
            <Button onClick={openEdit} disabled={!selectedRow || isConfiguring} loading={currentLoading}>Details</Button>
            <Button onClick={openDelete} disabled={!selectedRow || !bookingsFieldsEnabled || isConfiguring} loading={deleting}>Delete</Button>

            {/* Config — drag-reorder/resize columns */}
            {!isConfiguring ? (
              <Button htmlType="button" onClick={startConfiguring}>
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">{configButtonLabel}</Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledFlex>

        <Spin spinning={loadingList}>
          <DataTable
            headers={headers}
            draggable={isConfiguring}
            data={data}
            ref={tableRef}
            rowUniqueId="uuid"
            onRowClick={onRowClick}
            allowTableSorting
            autoResetSelection={false}
            selected={selectedRow === null}
          />
        </Spin>
      </StyledSubTableContainer>

      {isModalOpen && (
        <CustomModal
          size="lg"
          header={mode === "add" ? "Add Criteria Rule" : "Modify Criteria Rule"}
          show={isModalOpen}
          onHide={closeModal}
          onSaveClick={handleSave}
          confirmLoading={saving}
          backdrop={false}
          maskClosable={false}
          isDisabled={!bookingsFieldsEnabled}
        >
          <RulesCriteriaLocksPermitsForm
            form={form}
            initialValues={mode === "edit" ? selectedRow : EMPTY_INITIAL_VALUES}
            onFinish={onFinish}
            disabled={currentLoading}
          />
        </CustomModal>
      )}
    </>
  );
};

export default UserInfoCriteriaRulesControlling;
